<template>
  <main class="report-cashboxes">
    <h1 class="report-cashboxes__header">
      Отчет по кассе
    </h1>
    <form
      class="report-cashboxes__filters"
      v-if="hasFetchedOptions"
      v-on:submit.prevent
    >
      <div class="report-cashboxes__filters-wrapper">
        <date-range-with-presets
          v-bind:presets="presetsOfDates"
          v-bind:value="selectedDates"
          custom-color-date="black"
          v-on:input="onInputDates" />
        <multiple-autocomplete-input
          label="Наименование операции"
          multiple
          removal-per-chip
          small-chips
          v-bind:items="operationNamesOptions"
          v-model="selectedOperationNames"
          v-on:input="onChangeFilters"
        />
      </div>
      <div class="report-cashboxes__filters-wrapper">
        <multiple-autocomplete-input
          class="report-cashboxes__input-select"
          label="Касса"
          multiple
          removal-per-chip
          small-chips
          v-bind:items="cashboxOptions"
          v-model="selectedCashboxes"
          v-on:input="onChangeFilters"
        />
        <multiple-autocomplete-input
          class="report-cashboxes__input-select"
          label="Кассир"
          multiple
          removal-per-chip
          small-chips
          v-bind:items="employeeOptions"
          v-model="selectedCashiers"
          v-on:input="onChangeFilters"
        />
        <multiple-autocomplete-input
          v-if="isCashboxWithdrawal"
          class="report-cashboxes__input-select"
          label="Сотрудник"
          multiple
          removal-per-chip
          small-chips
          v-bind:items="employeeOptions"
          v-model="selectedEmployees"
          v-on:input="onChangeFilters"
        />
        <multiple-autocomplete-input
          v-if="isCashboxWithdrawal"
          class="report-cashboxes__input-select"
          label="Цель"
          multiple
          removal-per-chip
          small-chips
          v-bind:items="withdrawalPurposeOptions"
          v-model="selectedWithdrawalPurposes"
          v-on:input="onChangeFilters"
        />
      </div>
    </form>
    <div class="report-cashboxes__body">
      <table class="report-cashboxes-table report-cashboxes__table">
        <thead class="report-cashboxes-table__head-line">
          <tr>
            <th
              class="report-cashboxes-table__head-cell"
              v-bind:key="field"
              v-for="({ field, title }) in filterReportColumns"
            >
              <template>{{ title }}</template>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            class="report-cashboxes-table__body-line"
            v-bind:key="`table-rows:${rowIndex}`"
            v-for="(row, rowIndex) in filterReportRows"
          >
            <td
              class="report-cashboxes-table__body-cell"
              v-bind:key="`table-rows:${rowIndex}:cells:${cellIndex}`"
              v-for="(title, cellIndex) in row"
            >
              <span>{{ title }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </main>
</template>

<script>
import DateRangeWithPresets from '@/components/DateRangeWithPresets.vue';
import MultipleAutocompleteInput from '@/components/MultipleAutocompleteInput.vue';
import debounce from 'lodash.debounce';
import saveFileMixin from '@/mixins/saveFileMixin';
import { getISODateStringByDate } from '@/mixins/dateMixin';
import notifierMixin from '@/mixins/notifierMixin';

/**
 * @description Компонент отчета по кассе.
 * @type {Vue}
 */
export default {
  components: {
    DateRangeWithPresets,
    MultipleAutocompleteInput,
  },
  computed: {
    filterReportColumns() {
      return this.reportColumns.filter((item, index) => {
        if ([8, 9].includes(index)) {
          return this.isCashboxWithdrawal;
        }
        return true;
      });
    },
    filterReportRows() {
      return this.reportRows.map((item) => item.filter((cell, index) => {
        if ([8, 9].includes(index)) {
          return this.isCashboxWithdrawal;
        }
        return true;
      }));
    },
    /**
     * @description Расходы по кассам.
     * @returns {boolean}
     */
    isCashboxWithdrawal() {
      return this.selectedOperationNames.includes('cashbox-withdrawal');
    },
    /**
     * @description Набор предварительных дат для фильтрации.
     * @type {object[]}
     */
    presetsOfDates() {
      return this
        .periodOptions
        .map(({ from_to: [from, to], title, value }) => {
          const periodDates = [new Date(from), new Date(to)];
          return {
            id: value,
            label: title,
            title,
            toValue: () => periodDates,
          };
        });
    },
  },
  created() {
    this
      .fetchOptions()
      .then(() => {
        this
          .presetsOfDates
          .slice(2, 3)
          .forEach((candidate) => {
            this.selectedDates = candidate.toValue();
          });
        this.selectedOperationNames = this.operationNamesOptions.map(({ value }) => value);
        this.fetchReport();
      })
      .finally(() => { this.$nextTick(() => { this.hasFetchedOptions = true; }); });
  },
  data() {
    return {
      isFinishedReport: false,
      hasFetchedOptions: false,
      employeeOptions: [],
      cashboxOptions: [],
      withdrawalPurposeOptions: [],
      operationNamesOptions: [],
      periodOptions: [],
      reportColumns: [],
      reportId: null,
      reportRows: [],
      selectedDates: [],
      selectedCashiers: [],
      selectedCashboxes: [],
      selectedEmployees: [],
      selectedWithdrawalPurposes: [],
      selectedOperationNames: [],
      employeeId: null,
    };
  },
  methods: {
    debounceFetchReport: debounce(function _() { this.fetchReport(); }, 1000),
    /**
     * @description Получить опции отчета.
     * @returns {Promise}
     */
    fetchOptions() {
      return this
        .$reportGateway
        .getCashboxReportOptions()
        .then((result) => {
          result.forEach(({
            cashboxes,
            employees,
            period_types: periods,
            operation_names: operationNames,
            withdrawal_purpose: withdrawalPurpose,
          }) => {
            this.employeeOptions = employees;
            this.cashboxOptions = cashboxes;
            this.periodOptions = periods;
            this.operationNamesOptions = operationNames;
            this.withdrawalPurposeOptions = withdrawalPurpose;
          });
        });
    },
    /**
     * @description Получить данные отчета.
     * @returns {void}
     */
    fetchReport() {
      this.isFinishedReport = false;
      const [dateFrom, dateTo] = this.selectedDates.map(getISODateStringByDate);
      this
        .$reportGateway
        .getCashboxesReport(
          dateFrom,
          dateTo,
          this.selectedCashboxes,
          this.selectedCashiers,
          this.selectedEmployees,
          this.selectedOperationNames,
          this.selectedWithdrawalPurposes,
        )
        .then((result) => {
          result.forEach(({ columns, rows }) => {
            this.reportColumns = columns;
            this.reportRows = rows;
          });
        })
        .finally(() => { this.isFinishedReport = true; });
    },
    /**
     * @description Обработать выбор периода для отчета.
     * @param {Date[]} value
     * @returns {void}
     */
    onInputDates(value) {
      if (value.length === 2) {
        this.selectedDates = value.sort(
          (first, second) => Number(first.getTime() > second.getTime()),
        );
        this.debounceFetchReport();
      } else {
        this.selectedDates = value;
      }
    },
    /**
     * @description Обработать изменение фильтров.
     * @returns {void}
     */
    onChangeFilters() {
      if (this.isCashboxWithdrawal !== true) {
        this.selectedEmployees = [];
        this.selectedWithdrawalPurposes = [];
      }
      this.debounceFetchReport();
    },
  },
  mixins: [notifierMixin, saveFileMixin],
  name: 'ClinicPanelReportTreatmentPlans',
};
</script>

<style lang="scss">
@use "@/scss/buttons";
.report-cashboxes {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  overflow: auto;
  padding: 2rem 1rem;
  width: 100%;
  &__filters {
    display: flex;
    flex-direction: column;
    align-items: baseline;
    gap: 1rem;
    width: 100%;
  }
  &__filters-wrapper {
    display: flex;
    align-items: flex-end;
    flex-wrap: wrap;
    gap: 1rem;
    width: 100%;
  }
  &__input-select {
    flex: 1;
    min-width: 330px;
  }
  &-table {
    border-collapse: collapse;
    font-size: .875rem;
    line-height: 1rem;
    overflow: auto;
    table-layout: fixed;
    width: 100%;
    &__body-cell {
      padding: 15px 8px;
      text-align: left;
      text-transform: none;
      white-space: wrap;
      overflow-wrap: anywhere;
    }
    &__body-line {
      border-bottom: 1px solid var(--fill-color);
    }
    &__head-cell {
      padding: 10px 8px;
      text-align: left;
    }
    &__head-line {
      border-bottom: 2px solid var(--action-color);
      font-weight: 700;
    }
    &__sort {
      cursor: pointer;
      input {
        display: none;
      }
      &::after {
        background-image: url("@/assets/img/checked/checked-grey.svg");
        background-position: center;
        background-repeat: no-repeat;
        background-size: contain;
        content: '';
        display: inline-block;
        height: 8px;
        margin: 0 0 0 2px;
        min-height: 8px;
        min-width: 8px;
        width: 8px;
      }
      &.selected {
        color: var(--action-color);
      }
    }
  }
  &__button {
    @include buttons.btn-transparent(155px, 48px, 16px);
    margin: 40px 0 30px auto;
    flex-shrink: 0;
    &:before {
      content: "";
      background-image: url("@/assets/img/exel.svg");
      background-repeat: no-repeat;
      background-size: contain;
      width: 16px;
      height: 16px;
      margin: 0 8px 0 0;
    }
  }
}
@media screen and (max-width: 1380px) {
  .report-cashboxes {
    &__body {
      overflow: scroll;
    }
    &-table {
      width: 1400px;
    }
  }
}
@media screen and (max-width: 1260px) {
  .report-cashboxes {
    &__filters {
      flex-direction: column;
    }
  }
}

@media screen and (max-width: 480px) {
  .report-cashboxes {
    &__input-select {
      min-width: 100%;
    }
  }
}
</style>
